import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Handshake, Users, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import GeometricBackground from '@/components/GeometricBackground';

const PartnershipsPage: React.FC = () => {
  const benefits = [
    {
      icon: Handshake,
      title: 'Joint Ventures',
      description: 'Combine your audience and our platform to launch new music education products.',
    },
    {
      icon: Users,
      title: 'Schools & Academies',
      description: 'Offer Arrangelly as the digital backbone for your entire teaching organisation.',
    },
    {
      icon: Sparkles,
      title: 'AI Experiences',
      description: 'Co-create AI-powered exercises and tools tailored to your students.',
    },
  ] as const;

  return (
    <>
      <Helmet>
        <title>Partnerships - Arrangelly</title>
        <meta
          name="description"
          content="Partner with Arrangelly to bring a premium music teaching platform to your audience."
        />
      </Helmet>

      <div className="relative min-h-screen overflow-hidden">
        <GeometricBackground />
        <div className="relative z-10 py-20">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-violet-600 to-secondary bg-clip-text text-transparent">
                Grow Together with Arrangelly
              </h1>
              <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto">
                We collaborate with schools, academies, creators, and businesses who want to offer a
                complete music teaching system under their own brand.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
              {benefits.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="glass-panel rounded-[1.75rem] p-6 border border-violet-100 shadow-lg"
                >
                  <div className="w-10 h-10 rounded-[1rem] bg-gradient-to-br from-primary to-secondary flex items-center justify-center mb-4">
                    <item.icon className="w-5 h-5 text-primary-foreground" />
                  </div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-2">{item.title}</h2>
                  <p className="text-slate-600 text-sm">{item.description}</p>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="max-w-3xl mx-auto glass-panel rounded-[1.75rem] p-8 text-center border border-violet-100"
            >
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                Let&apos;s design your partnership together
              </h2>
              <p className="text-slate-600 mb-6">
                Tell us about your idea, audience, and goals, and we&apos;ll propose a partnership
                model with revenue sharing and a fully managed technical stack.
              </p>
              <Button
                asChild
                className="h-11 px-8 bg-accent hover:bg-accent/90 text-accent-foreground rounded-full text-sm font-semibold"
              >
                <a href="/contact">Talk to us about a partnership</a>
              </Button>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PartnershipsPage;


